import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ArrowLeft, Search, Eye, Trash2, Building2 } from "lucide-react";
import { PinVerificationDialog } from "@/components/PinVerificationDialog";
import { Skeleton } from "@/components/ui/skeleton";
import { getErrorMessage } from "@/lib/errorHandler";
import { BulkBranchAssignment } from "@/components/BulkBranchAssignment";

interface Student {
  id: string;
  calling_name: string | null;
  first_name: string;
  last_name: string;
  office_name: string;
  passport_number: string;
  class_1_medical: string;
  class_2_medical: string;
  date_of_joining: string;
  computer_number_status: string;
  computer_number: string | null;
  batch_id: string | null;
  branch_id: string | null;
  batches?: {
    name: string;
  };
  student_subjects?: Array<{
    subject_id: string;
    status: string;
    exam_result: string | null;
    subjects: {
      name: string;
    };
  }>;
}

interface Batch {
  id: string;
  name: string;
}

const StudentList = () => {
  const navigate = useNavigate();
  const [userName, setUserName] = useState("");
  const [students, setStudents] = useState<Student[]>([]);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [branches, setBranches] = useState<Batch[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<string>(() => sessionStorage.getItem("student-sort") || "name");
  const [filterBatch, setFilterBatch] = useState<string>(() => sessionStorage.getItem("student-filter-batch") || "all");
  const [filterComputerStatus, setFilterComputerStatus] = useState<string>(() => sessionStorage.getItem("student-filter-computer") || "all");
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState<{ id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);

  // Restore scroll position after content loads
  useEffect(() => {
    if (!loading) {
      const savedScrollPosition = sessionStorage.getItem('studentListScrollPosition');
      if (savedScrollPosition) {
        // Use setTimeout to ensure DOM is fully rendered
        setTimeout(() => {
          window.scrollTo(0, parseInt(savedScrollPosition));
          sessionStorage.removeItem('studentListScrollPosition');
        }, 100);
      }
    }
  }, [loading]);

  useEffect(() => {
    let isMounted = true;
    let fallbackRedirect: ReturnType<typeof setTimeout> | null = null;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return;

      // Only redirect on explicit sign-out events
      if (event === 'SIGNED_OUT') {
        navigate('/auth', { replace: true });
      }

      // If we scheduled a fallback redirect but the user signed in, cancel it
      if ((event === 'SIGNED_IN' || (event === 'INITIAL_SESSION' && session))) {
        if (fallbackRedirect) {
          clearTimeout(fallbackRedirect);
          fallbackRedirect = null;
        }
      }

      // If initial session is null, schedule a slower fallback redirect
      if (event === 'INITIAL_SESSION' && !session && !fallbackRedirect) {
        fallbackRedirect = setTimeout(() => navigate('/auth', { replace: true }), 1500);
      }
    });

    const initialize = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          // Wait for auth listener to decide; do not navigate immediately
          return;
        }

        if (!isMounted) return;

        const { data: profile } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('id', session.user.id)
          .single();

        if (!isMounted) return;
        if (profile) setUserName(profile.full_name);

        const [{ data: isAdminUser }, { data: isManagerUser }] = await Promise.all([
          supabase.rpc('has_role', { _user_id: session.user.id, _role: 'admin' }),
          supabase.rpc('has_role', { _user_id: session.user.id, _role: 'manager' }),
        ]);
        if (isMounted) setIsAdmin(Boolean(isAdminUser || isManagerUser));

        // Now that auth is confirmed, fetch students and batches
        await Promise.all([fetchStudents(), fetchBatches()]);
      } catch (error) {
        console.error('Error initializing Students page:', error);
      }
    };

    initialize();

    return () => {
      isMounted = false;
      if (fallbackRedirect) clearTimeout(fallbackRedirect);
      subscription.unsubscribe();
    };
  }, [navigate]);

  const fetchStudents = async () => {
    try {
      const { data, error } = await supabase
        .from("students")
        .select(`
          *,
          batches!batch_id (
            name
          ),
          student_subjects (
            subject_id,
            status,
            exam_result,
            subjects (
              name
            )
          )
        `)
        .order("first_name");

      if (error) throw error;
      setStudents(data || []);
    } catch (error) {
      console.error("Error fetching students:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBatches = async () => {
    try {
      const { data, error } = await supabase
        .from("batches")
        .select("id, name")
        .order("name");

      if (error) throw error;
      setBatches(data || []);
      
      // Also set branches (IC Calicut, IC Bangalore)
      const branchData = (data || []).filter(b => 
        b.name.startsWith("IC ") || b.name.includes("Calicut") || b.name.includes("Bangalore")
      );
      setBranches(branchData);
    } catch (error) {
      console.error("Error fetching batches:", error);
    }
  };

  const toggleStudentSelection = (studentId: string) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedStudents.length === filteredAndSortedStudents.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(filteredAndSortedStudents.map(s => s.id));
    }
  };

  const handleDeleteClick = (studentId: string, studentName: string) => {
    if (!confirm(`Are you sure you want to delete ${studentName}? This action cannot be undone.`)) {
      return;
    }
    setStudentToDelete({ id: studentId, name: studentName });
    setPinDialogOpen(true);
  };

  const handleDeleteConfirm = async (pin: string) => {
    if (!studentToDelete) return;

    try {
      setDeleting(true);
      
      // Verify PIN using server-side function
      const { data: isVerified, error: verifyError } = await supabase.rpc('verify_admin_action', {
        _pin: pin,
        _action: 'Delete Student',
        _details: {
          student_id: studentToDelete.id,
          student_name: studentToDelete.name
        }
      });

      if (verifyError || !isVerified) {
        toast.error(getErrorMessage(verifyError));
        return;
      }

      const { error } = await supabase
        .from("students")
        .delete()
        .eq("id", studentToDelete.id);

      if (error) throw error;
      
      toast.success("Student deleted successfully");
      setPinDialogOpen(false);
      setStudentToDelete(null);
      fetchStudents();
    } catch (error: any) {
      toast.error(getErrorMessage(error));
    } finally {
      setDeleting(false);
    }
  };

  const getExamCount = (student: Student) => {
    return student.student_subjects?.filter(ss => ss.exam_result === 'Pass').length || 0;
  };

  const getOngoingCount = (student: Student) => {
    return student.student_subjects?.filter(ss => ss.status === 'Ongoing').length || 0;
  };

  // Persist filter states
  useEffect(() => {
    sessionStorage.setItem("student-sort", sortBy);
  }, [sortBy]);

  useEffect(() => {
    sessionStorage.setItem("student-filter-batch", filterBatch);
  }, [filterBatch]);

  useEffect(() => {
    sessionStorage.setItem("student-filter-computer", filterComputerStatus);
  }, [filterComputerStatus]);

  const filteredAndSortedStudents = students
    .filter((student) => {
      // Search filter
      const searchMatch = `${student.calling_name || student.office_name} ${student.first_name} ${student.last_name} ${student.passport_number}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      
      // Batch filter
      const batchMatch = filterBatch === "all" || student.batch_id === filterBatch;
      
      // Computer status filter - special handling for "Pending" to only show students without computer number
      let computerMatch = true;
      if (filterComputerStatus !== "all") {
        if (filterComputerStatus === "Pending") {
          computerMatch = !student.computer_number || student.computer_number.trim() === "";
        } else {
          computerMatch = student.computer_number_status === filterComputerStatus;
        }
      }
      
      return searchMatch && batchMatch && computerMatch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "name":
          return (a.calling_name || a.office_name).localeCompare(b.calling_name || b.office_name);
        case "computer_pending":
          // Sort by whether they have a computer number or not
          const hasNumberA = a.computer_number && a.computer_number.trim() !== "";
          const hasNumberB = b.computer_number && b.computer_number.trim() !== "";
          if (!hasNumberA && hasNumberB) return -1;
          if (hasNumberA && !hasNumberB) return 1;
          return 0;
        case "exams_done":
          return getExamCount(b) - getExamCount(a);
        case "ongoing_subjects":
          return getOngoingCount(b) - getOngoingCount(a);
        case "batch":
          const batchA = a.batches?.name || "";
          const batchB = b.batches?.name || "";
          return batchA.localeCompare(batchB);
        default:
          return 0;
      }
    });

  const getMedicalBadgeVariant = (status: string) => {
    switch (status) {
      case "Done":
        return "default";
      case "Pending":
        return "secondary";
      case "Expired":
        return "destructive";
      default:
        return "outline";
    }
  };

  return (
    <Layout userName={userName}>
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <Button
              variant="ghost"
              onClick={() => navigate("/dashboard")}
              className="mb-2"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
            <h2 className="text-3xl font-bold">Student Progress Tracker</h2>
            <p className="text-muted-foreground">View and manage all students</p>
          </div>
          {isAdmin && (
            <div className="flex gap-2">
              {selectedStudents.length > 0 && (
                <Button 
                  variant="outline" 
                  onClick={() => setBulkAssignOpen(true)}
                >
                  <Building2 className="h-4 w-4 mr-2" />
                  Assign Branch ({selectedStudents.length})
                </Button>
              )}
              <Button onClick={() => navigate("/students/add")}>
                Add New Student
              </Button>
            </div>
          )}
        </div>

        {/* Search and Filters - Cleaner Layout */}
        <div className="mb-6 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or passport number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-11"
            />
          </div>
          
          <div className="flex flex-wrap gap-2 items-center">
            <Select value={filterBatch} onValueChange={setFilterBatch}>
              <SelectTrigger className="w-[150px] h-9">
                <SelectValue placeholder="Batch" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Batches</SelectItem>
                {batches.map(batch => (
                  <SelectItem key={batch.id} value={batch.id}>
                    {batch.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filterComputerStatus} onValueChange={setFilterComputerStatus}>
              <SelectTrigger className="w-[160px] h-9">
                <SelectValue placeholder="Computer Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Pending">Pending</SelectItem>
                <SelectItem value="Applied">Applied</SelectItem>
                <SelectItem value="Approved">Approved</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[150px] h-9">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Name</SelectItem>
                <SelectItem value="batch">Batch</SelectItem>
                <SelectItem value="exams_done">Exams Passed</SelectItem>
                <SelectItem value="computer_pending">Computer Pending</SelectItem>
              </SelectContent>
            </Select>

            {(filterBatch !== "all" || filterComputerStatus !== "all" || searchTerm) && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => {
                  setSortBy("name");
                  setFilterBatch("all");
                  setFilterComputerStatus("all");
                  setSearchTerm("");
                  sessionStorage.removeItem("student-sort");
                  sessionStorage.removeItem("student-filter-batch");
                  sessionStorage.removeItem("student-filter-computer");
                }}
              >
                Clear
              </Button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : filteredAndSortedStudents.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">
                {searchTerm || filterBatch !== "all" || filterComputerStatus !== "all" 
                  ? "No students found matching your filters." 
                  : "No students added yet."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="mb-6 sm:mb-8">
            <p className="text-sm text-muted-foreground">
              Showing {filteredAndSortedStudents.length} student{filteredAndSortedStudents.length !== 1 ? 's' : ''}
            </p>
            {isAdmin && (
              <div className="flex items-center gap-4 mt-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={selectedStudents.length === filteredAndSortedStudents.length && filteredAndSortedStudents.length > 0}
                    onCheckedChange={toggleSelectAll}
                  />
                  <span className="text-sm">Select All</span>
                </div>
                {selectedStudents.length > 0 && (
                  <Button 
                    size="sm"
                    onClick={() => setBulkAssignOpen(true)}
                  >
                    <Building2 className="h-4 w-4 mr-2" />
                    Assign to Branch ({selectedStudents.length})
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
        
        {!loading && filteredAndSortedStudents.length > 0 && (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b bg-muted/50">
                    <tr>
                      {isAdmin && <th className="p-3 w-10"></th>}
                      <th className="p-3 text-left text-sm font-medium">Student</th>
                      <th className="p-3 text-center text-sm font-medium">Computer #</th>
                      <th className="p-3 text-center text-sm font-medium hidden sm:table-cell">Class 1</th>
                      <th className="p-3 text-center text-sm font-medium hidden sm:table-cell">Class 2</th>
                      <th className="p-3 text-center text-sm font-medium hidden md:table-cell">Exams</th>
                      <th className="p-3 w-20"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredAndSortedStudents.map((student) => {
                      const passedExams = getExamCount(student);
                      const totalSubjects = student.student_subjects?.length || 0;
                      
                      return (
                        <tr 
                          key={student.id} 
                          className={`hover:bg-muted/30 transition-colors ${selectedStudents.includes(student.id) ? 'bg-primary/5' : ''}`}
                        >
                          {isAdmin && (
                            <td className="p-3">
                              <Checkbox
                                checked={selectedStudents.includes(student.id)}
                                onCheckedChange={() => toggleStudentSelection(student.id)}
                              />
                            </td>
                          )}
                          <td className="p-3">
                            <div className="flex items-center gap-3">
                              <Avatar className="h-8 w-8">
                                <AvatarFallback className="text-xs">
                                  {(student.calling_name || student.office_name || student.first_name).substring(0, 2).toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium text-sm">
                                  {student.calling_name || student.office_name}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {student.batches?.name || 'No Batch'}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="p-3 text-center">
                            {student.computer_number ? (
                              <Badge variant="default" className="text-xs">
                                {student.computer_number}
                              </Badge>
                            ) : (
                              <Badge variant={student.computer_number_status === 'Approved' ? 'secondary' : 'destructive'} className="text-xs">
                                {student.computer_number_status || 'Pending'}
                              </Badge>
                            )}
                          </td>
                          <td className="p-3 text-center hidden sm:table-cell">
                            <Badge 
                              variant={student.class_1_medical === 'Done' ? 'default' : student.class_1_medical === 'Pending' ? 'secondary' : 'destructive'} 
                              className="text-xs"
                            >
                              {student.class_1_medical}
                            </Badge>
                          </td>
                          <td className="p-3 text-center hidden sm:table-cell">
                            <Badge 
                              variant={student.class_2_medical === 'Done' ? 'default' : student.class_2_medical === 'Pending' ? 'secondary' : 'destructive'} 
                              className="text-xs"
                            >
                              {student.class_2_medical}
                            </Badge>
                          </td>
                          <td className="p-3 text-center hidden md:table-cell">
                            <span className="text-sm font-medium">{passedExams}/{totalSubjects}</span>
                          </td>
                          <td className="p-3">
                            <div className="flex justify-end gap-1">
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8"
                                onClick={() => {
                                  sessionStorage.setItem('studentListScrollPosition', window.scrollY.toString());
                                  navigate(`/students/${student.id}`);
                                }}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              {isAdmin && (
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8 text-destructive hover:text-destructive"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteClick(student.id, student.calling_name || student.office_name);
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        <PinVerificationDialog
          open={pinDialogOpen}
          onOpenChange={setPinDialogOpen}
          onConfirm={handleDeleteConfirm}
          title="Delete Student"
          description={`Enter your PIN to confirm deletion of ${studentToDelete?.name}`}
          confirmLabel="Delete Student"
          loading={deleting}
        />

        <BulkBranchAssignment
          open={bulkAssignOpen}
          onOpenChange={setBulkAssignOpen}
          selectedIds={selectedStudents}
          branches={branches}
          tableName="students"
          onComplete={() => {
            setSelectedStudents([]);
            fetchStudents();
          }}
        />
      </div>
    </Layout>
  );
};

export default StudentList;